import { SearchIcon } from '@/components/icons';
import type { DomainSelectItem } from '@/services/inventory-api.service';
import { inventoryApi } from '@/services/inventory-api.service';
import { classNames } from '@/utils/classNames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './DomainPreStep.module.css';

const formatDomainName = (domain: DomainSelectItem): string => domain.value ?? '';

type DomainPreStepProps = {
  resortName?: string;
  onConfirm: (domain: DomainSelectItem) => void;
};

export const DomainPreStep = ({ resortName, onConfirm }: DomainPreStepProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [domains, setDomains] = useState<DomainSelectItem[]>([]);
  const [pendingDomain, setPendingDomain] = useState<DomainSelectItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await inventoryApi.getDomains();
        setDomains(data);
      } catch {
        setError('Failed to load ski areas');
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const filteredDomains = useMemo(() => {
    if (!searchQuery) return domains;
    const q = searchQuery.toLowerCase();
    return domains.filter((d) => (d.value ?? '').toLowerCase().includes(q));
  }, [domains, searchQuery]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!pendingDomain) return;
    onConfirm(pendingDomain);
  }, [pendingDomain, onConfirm]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {resortName && (
          <p className={styles.goingTo}>
            Going to: <strong>{resortName}</strong>
          </p>
        )}
        <h2 className={styles.title}>Where do you plan to ski?</h2>
      </div>

      <div className={styles.card}>
        <div className={styles.searchContainer}>
          <SearchIcon className={styles.searchIcon} />
          <input
            ref={searchInputRef}
            type="text"
            className={styles.searchInput}
            placeholder="Search ski area"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>

        <div className={styles.list}>
          {isLoading && (
            <div className={styles.emptyState}>
              <p>Loading ski areas...</p>
            </div>
          )}

          {error && !isLoading && (
            <div className={styles.emptyState}>
              <p className={styles.errorText}>{error}</p>
              <button
                type="button"
                className={styles.retryButton}
                onClick={() => {
                  setError(null);
                  const load = async () => {
                    setIsLoading(true);
                    try {
                      const data = await inventoryApi.getDomains();
                      setDomains(data);
                    } catch {
                      setError('Failed to load ski areas');
                    } finally {
                      setIsLoading(false);
                    }
                  };
                  void load();
                }}
              >
                Try again
              </button>
            </div>
          )}

          {!isLoading && !error && filteredDomains.length === 0 && searchQuery && (
            <div className={styles.emptyState}>
              <p>No ski areas found</p>
              <p className={styles.emptyStateSubtext}>Try adjusting your search</p>
            </div>
          )}

          {!isLoading && !error && filteredDomains.map((domain) => (
            <button
              key={domain.id}
              type="button"
              className={classNames(
                styles.item,
                pendingDomain?.id === domain.id && styles.selected
              )}
              onClick={() => {
                setPendingDomain(domain);
              }}
            >
              {formatDomainName(domain)}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.continueButton}
          onClick={handleConfirm}
          disabled={!pendingDomain}
        >
          Continue
        </button>
      </div>
    </div>
  );
};
